import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Copy, Mail, MessageCircle, Share2, X } from "lucide-react";
import { issueConfirmationLink } from "../../../api/purchases";

const Btn = ({ onClick, icon: Icon, label, tone, tid }) => (
  <button onClick={onClick} data-testid={tid}
          className={`flex items-center justify-center gap-2 rounded-2xl border border-border/60 px-3 py-3 text-xs font-bold shadow-neu-sm transition hover:brightness-110 ${tone}`}>
    <Icon className="h-4 w-4" /> {label}
  </button>
);

// نافذة مشاركة رابط تأكيد المورد: واتساب · بريد · نسخ · مشاركة الجهاز.
export const ShareLinkModal = ({ open, onClose, doc, onDone }) => {
  const { t } = useTranslation();
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const cardUrl = (tok) => `${window.location.origin}/api/po/${tok}`;
    if (doc.confirmation_token) {
      setLink(cardUrl(doc.confirmation_token));
      return;
    }
    setBusy(true);
    issueConfirmationLink(doc.id)
      .then((res) => {
        const tok = (res.url || "").split("/").pop();
        setLink(cardUrl(tok));
        onDone?.();
      })
      .catch((e) => toast.error(e?.response?.data?.detail?.code
                                || t("errors.generic")))
      .finally(() => setBusy(false));
  }, [open, doc.confirmation_token, doc.id, onDone, t]);

  if (!open) return null;
  const msg = `${t("purchases.confirm.shareText")} ${doc.number || ""}: ${link}`;
  const cp = async () => {
    await navigator.clipboard.writeText(link);
    toast.success(t("purchases.confirm.copied"));
  };
  const em = () => window.open(
    `mailto:${doc.supplier?.email || ""}?subject=${encodeURIComponent(doc.number || "PO")}&body=${encodeURIComponent(msg)}`);
  const sh = async () => (navigator.share
    ? navigator.share({ title: doc.number || "PO", text: msg }) : cp());

  return (
    <div dir="rtl" data-testid="share-link-modal"
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl bg-background p-5 shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-start justify-between gap-2">
          <p className="text-base font-bold text-primary">{t("purchases.share.title")}</p>
          <button data-testid="share-modal-close" onClick={onClose}
                  className="shrink-0 rounded-full p-1 hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">
          {t("purchases.share.subtitle")}
        </p>
        <p className="mb-1 text-[11px] font-bold text-foreground">{t("purchases.share.linkLabel")}</p>
        <p className="mb-4 min-h-[2.5rem] break-all rounded-xl bg-muted p-2 num font-mono text-[10px]"
           data-testid="share-modal-link">{busy ? "…" : link}</p>
        <div className="grid grid-cols-2 gap-2">
          <Btn tid="share-whatsapp" icon={MessageCircle} label={t("purchases.share.whatsapp")}
               tone="bg-emerald-500 text-white border-emerald-600"
               onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank")} />
          <Btn tid="share-email" icon={Mail} label={t("purchases.share.email")}
               tone="bg-sky-500 text-white border-sky-600" onClick={em} />
          <Btn tid="share-copy" icon={Copy} label={t("purchases.share.copy")}
               tone="bg-primary text-primary-foreground" onClick={cp} />
          <Btn tid="share-device" icon={Share2} label={t("purchases.share.device")}
               tone="bg-slate-700 text-white border-slate-800" onClick={sh} />
        </div>
      </div>
    </div>
  );
};
